import { Container, Graphics, InteractionEvent, LINE_JOIN, Rectangle, Text } from "pixi.js"
import { SCALER } from "../../../../client_util"
import { DEFAULT_FONT_FAMILY, TEXT_ROLES } from "../text/typography"
import { chamfer, drawBoard, drawPlot, footprint, Footprint, footprintStroke, plotGrid,
    FACE, GOLD, GLYPH, INK, MUTED, RIM, TIMBER, TIMBER_EDGE } from "./build_board"

export type MobileBuildTool = "single" | "line" | "room"
export interface MobileBuildState {
    available: boolean
    active: boolean
    tool: MobileBuildTool
    canUndo: boolean
    material: string
}

// The phone's build controls: the same plan board as the desktop shape palette (see
// build_board.ts), laid out for thumbs instead of a cursor.
//
// A tool is a plot carrying the footprint it lays down and the word for it -- on a phone the word
// does the work and the drawing says which of the three it is at a glance, where on the desktop the
// drawing is the whole control. Undo sits on the same row as a plate, not a plot, because it is not
// something you can be standing in.
const PANEL_W = 296
const PANEL_H = 70
const CELL_W = 66
const CELL_H = 50
const CELL_Y = 10
const UNDO_X = 220
const ENTER_W = 90
const ENTER_H = 42

// What each tool draws on its plot.
const TOOL_FOOTPRINT: Record<MobileBuildTool, Footprint> = { single: "single", line: "line", room: "rectangle" }

/** A tool on the dock: a plot of squared paper with its footprint on it and its name beneath. */
class ToolCell extends Graphics {
    private selected = false
    private enabled = true
    private caption: Text

    constructor(private label: string, private kind: Footprint, onTap: () => void) {
        super()
        this.caption = new Text(label, { fontFamily: DEFAULT_FONT_FAMILY,
            fontSize: TEXT_ROLES.compactBody.fontSize, fill: GLYPH })
        this.addChild(this.caption)
        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(0, 0, CELL_W, CELL_H)
        this.on("pointertap", (event: InteractionEvent) => {
            event?.stopPropagation()
            if (this.enabled) onTap()
        })
        this.draw()
    }

    // The mobile toolbar is read back by label in gui_manager's frame tests, and a control that
    // cannot say what it is would be invisible to them.
    getLabel = () => this.label

    setSelected = (selected: boolean) => {
        if (selected === this.selected) return
        this.selected = selected
        this.draw()
    }

    setEnabled = (enabled: boolean) => {
        if (enabled === this.enabled) return
        this.enabled = enabled
        this.interactive = enabled
        this.cursor = enabled ? "pointer" : "default"
        this.draw()
    }

    protected draw = () => {
        const on = this.selected
        const ink = on ? GOLD : GLYPH
        const cy = 18
        this.clear()
        drawPlot(this, CELL_W, CELL_H, on, false)
        plotGrid(this, { x: 4, y: 4, w: CELL_W - 8, h: 26 }, CELL_W / 2, cy, on)
        footprintStroke(this, ink)
        footprint(this, this.kind, CELL_W / 2, cy)
        this.caption.style.fill = on ? INK : GLYPH
        this.caption.position.set((CELL_W - this.caption.width) / 2, 33)
        this.alpha = this.enabled ? 1 : 0.42
    }
}

/**
 * A verb rather than a place to stand. Undo and the way into build mode both use it.
 *
 * `primary` is the one that stands on the grass by itself, so it is cut from timber like a board of
 * its own and carries the gold -- it is the only control on the screen that has to be FOUND. Undo
 * is already on a board, so it is just a plate let into it; a second timber frame there reads as a
 * board on a board.
 */
class BoardButton extends Graphics {
    private enabled = true
    private caption: Text

    constructor(private label: string, private w: number, private h: number,
        private primary: boolean, onTap: () => void) {
        super()
        this.caption = new Text(label.toUpperCase(), { fontFamily: DEFAULT_FONT_FAMILY,
            fontSize: TEXT_ROLES.compactBody.fontSize, fill: INK, letterSpacing: 1 })
        this.addChild(this.caption)
        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(0, 0, w, h)
        this.on("pointertap", (event: InteractionEvent) => {
            event?.stopPropagation()
            if (this.enabled) onTap()
        })
        this.draw()
    }

    getLabel = () => this.label

    setEnabled = (enabled: boolean) => {
        if (enabled === this.enabled) return
        this.enabled = enabled
        this.interactive = enabled
        this.cursor = enabled ? "pointer" : "default"
        this.draw()
    }

    private draw = () => {
        this.clear()
        if (this.primary) {
            this.lineStyle({ width: 3, color: GOLD, alpha: 0.14, join: LINE_JOIN.MITER })
            chamfer(this, 1, 1, this.w - 2, this.h - 2, 5)
            this.lineStyle({ width: 1.5, color: TIMBER_EDGE, alpha: 0.95, join: LINE_JOIN.MITER })
            this.beginFill(TIMBER, 0.98)
            chamfer(this, 0.75, 0.75, this.w - 1.5, this.h - 1.5, 5)
            this.endFill()
        }
        const inset = this.primary ? 3.5 : 0.5
        this.lineStyle({ width: 1, color: this.primary ? GOLD : RIM, alpha: this.primary ? 1 : 0.7, join: LINE_JOIN.MITER })
        this.beginFill(FACE, this.primary ? 0.9 : 0.75)
        chamfer(this, inset, inset, this.w - inset * 2, this.h - inset * 2, 4)
        this.endFill()
        this.caption.style.fill = this.primary ? GOLD : MUTED
        this.caption.position.set((this.w - this.caption.width) / 2, (this.h - this.caption.height) / 2)
        this.alpha = this.enabled ? 1 : 0.42
    }
}

/** A mobile-only dock; the normal desktop palette never uses this layout. */
export class MobileBuildToolbar extends Container {
    private panel = new Container()
    private enter: BoardButton
    private tools = new Map<MobileBuildTool, ToolCell>()
    private undo: BoardButton
    private signature = ""

    constructor(actions: { toggle: (on: boolean) => void; select: (tool: MobileBuildTool) => void;
        undo: () => void }) {
        super()
        this.scale.y = 1 / SCALER
        this.zIndex = 60003
        this.enter = new BoardButton("Build", ENTER_W, ENTER_H, true, () => actions.toggle(true))
        this.addChild(this.enter, this.panel)
        const background = new Graphics()
        drawBoard(background, PANEL_W, PANEL_H)
        this.panel.interactive = true
        this.panel.hitArea = new Rectangle(0, 0, PANEL_W, PANEL_H)
        this.panel.addChild(background)
        ;(["single", "line", "room"] as MobileBuildTool[]).forEach((tool, index) => {
            const cell = new ToolCell(tool[0].toUpperCase() + tool.slice(1), TOOL_FOOTPRINT[tool],
                () => actions.select(tool))
            cell.position.set(10 + index * 70, CELL_Y)
            this.tools.set(tool, cell)
            this.panel.addChild(cell)
        })
        this.undo = new BoardButton("Undo", CELL_W, CELL_H, false, actions.undo)
        this.undo.position.set(UNDO_X, CELL_Y)
        this.panel.addChild(this.undo)
    }

    update = (state: MobileBuildState, readiedBar: { x: number; y: number }) => {
        this.visible = state.available || state.active
        this.panel.visible = state.active
        this.enter.visible = !state.active
        // The tray shares our parent; compensate for our vertical scale to keep an 8-unit gap.
        const aboveTray = (readiedBar.y - 8) * SCALER
        this.panel.position.set(readiedBar.x, aboveTray - PANEL_H)
        this.enter.position.set(readiedBar.x, aboveTray - ENTER_H)
        const signature = `${state.tool}:${state.canUndo}`
        if (signature !== this.signature) {
            this.signature = signature
            this.tools.forEach((cell, tool) => cell.setSelected(tool === state.tool))
            this.undo.setEnabled(state.canUndo)
        }
    }
}
